import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { LabelTemplateService, LabelTemplateSummary } from './label-template.service';

/** One entry of a print menu, in the shape those menus already use. */
export interface PrintMenuOption {
  name: string;
  value: string | number;
  templateFile: string;
}

/**
 * The customer's own labels, as print-menu entries.
 *
 * The four print menus in the app (product print, activity, storage tree,
 * reports) each hold a hard-coded list of the shipped DYMO labels. A designed
 * template is appended to that same DYMO group rather than given a group of
 * its own, so every menu picks it up without a template change and the
 * customer sees their labels next to the built-in ones.
 *
 * The value is `custom:<tplID>` instead of a number. printSelected() in
 * printerCode.js reads that prefix and routes to the render endpoint; the
 * numeric values keep meaning what they always meant.
 */
@Injectable({ providedIn: 'root' })
export class LabelPrintMenuService {
  /**
   * Cached for the session: a print menu is opened over and over while
   * labelling a shelf, and the template list changes only in the designer.
   */
  private cache: { [subject: string]: Observable<PrintMenuOption[]> } = {};

  constructor(private templates: LabelTemplateService) {}

  /**
   * Every DYMO template the customer owns.
   *
   * Deliberately NOT filtered by subject. Subject is copied from whatever
   * template was duplicated and is invisible in the designer, so a customer who
   * duplicated the Asset Label got a template marked 'asset' — which no menu
   * asked for, and their new label simply never appeared anywhere. A label the
   * customer made is a label they expect to see; a field that happens to be
   * empty on it is a far smaller problem than one they cannot find.
   *
   * @param subject Kept for callers that genuinely want one kind; unused today.
   */
  options(subject?: string): Observable<PrintMenuOption[]> {
    const key = subject || 'all';

    if (!this.cache[key]) {
      this.cache[key] = this.templates.list({ engine: 'dymo', subject }).pipe(
        map((res: any) => (res?.templates || [])
          // System templates are already in the menu by hand; offering them
          // twice under slightly different names would only confuse.
          .filter((t: LabelTemplateSummary) => t.kind === 'custom')
          .map((t: LabelTemplateSummary) => ({
            name: t.name,
            value: 'custom:' + t.tplID,
            templateFile: ''
          }))),
        shareReplay(1),
        /* catchError sits OUTSIDE the replay on purpose: inside it, a single
           timeout would be cached as "this customer has no labels" for the
           rest of the session. Here the failure is not stored, so the next
           menu asks again — while this one still opens with the built-in
           labels, which is what most printing uses anyway. */
        catchError(() => {
          delete this.cache[key];
          return of([] as PrintMenuOption[]);
        })
      );
    }

    return this.cache[key];
  }

  /** Called after the designer saves, so the next menu shows the new label. */
  invalidate(): void {
    this.cache = {};
  }

  /**
   * Appends the customer's labels to the DYMO group of a menu list, in place.
   * Returns the same list so a caller can assign it in one line.
   */
  appendTo(printLabelList: any[], subject?: string): Observable<any[]> {
    return this.options(subject).pipe(
      map((options) => {
        if (!options.length) {
          return printLabelList;
        }
        const dymoGroup = printLabelList.find((group) => group && group['DYMO']);
        if (dymoGroup) {
          // Rebuilt rather than pushed onto: these menu lists are module-level
          // singletons, so pushing would stack duplicates every time a dialog
          // is opened.
          dymoGroup['DYMO'] = dymoGroup['DYMO']
            .filter((label: PrintMenuOption) => String(label.value).indexOf('custom:') !== 0)
            .concat(options);
        }
        return printLabelList;
      })
    );
  }
}
